import { useState } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Stack, FormControl,
  InputLabel, Select, MenuItem, Divider, CircularProgress, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, Chip
} from '@mui/material';
import { Download, Refresh, FolderZip } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ATS() {
  const [tab, setTab] = useState(0);
  // Individual
  const [mes, setMes] = useState(String(new Date().getMonth() + 1));
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  // Semestral (RIMPE)
  const [semestre, setSemestre] = useState('06');
  const [anioSemestral, setAnioSemestral] = useState(String(new Date().getFullYear()));
  const [loadingSemestral, setLoadingSemestral] = useState(false);
  const [resultadoSemestral, setResultadoSemestral] = useState(null);
  const [errorSemestral, setErrorSemestral] = useState('');
  // Masivo
  const [mesDesde, setMesDesde] = useState('1');
  const [mesHasta, setMesHasta] = useState('6');
  const [anioMasivo, setAnioMasivo] = useState(String(new Date().getFullYear()));
  const [loadingMasivo, setLoadingMasivo] = useState(false);
  const [resultadoMasivo, setResultadoMasivo] = useState(null);
  const [errorMasivo, setErrorMasivo] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Generar individual
  const generar = async () => {
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const res = await axios.get(`${API_URL}/api/ats/generar?mes=${mes}&anio=${anio}`, { headers: headers() });
      setResultado(res.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al generar ATS');
    }
    setLoading(false);
  };

  const descargarXML = () => {
    if (!resultado?.xml) return;
    const blob = new Blob([resultado.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ATS_${anio}_${mes.padStart(2, '0')}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generar semestral (RIMPE) — un solo XML con 6 meses
  const generarSemestral = async () => {
    setLoadingSemestral(true);
    setErrorSemestral('');
    setResultadoSemestral(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/ats/generar?mes=${semestre}&anio=${anioSemestral}&semestral=true`,
        { headers: headers() }
      );
      setResultadoSemestral(res.data?.data);
    } catch (err) {
      setErrorSemestral(err.response?.data?.message || 'Error al generar ATS semestral');
    }
    setLoadingSemestral(false);
  };

  const descargarXMLSemestral = () => {
    if (!resultadoSemestral?.xml) return;
    const blob = new Blob([resultadoSemestral.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ATS_${anioSemestral}_${semestre}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generar masivo - preview
  const generarMasivoPreview = async () => {
    setLoadingMasivo(true);
    setErrorMasivo('');
    setResultadoMasivo(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/ats/generar-masivo?mesDesde=${mesDesde}&mesHasta=${mesHasta}&anio=${anioMasivo}&preview=true`,
        { headers: headers() }
      );
      setResultadoMasivo(res.data?.data);
    } catch (err) {
      setErrorMasivo(err.response?.data?.message || 'Error al generar vista previa');
    }
    setLoadingMasivo(false);
  };

  // Descargar ZIP masivo
  const descargarZIP = async () => {
    setLoadingMasivo(true);
    setErrorMasivo('');
    try {
      const res = await axios.get(
        `${API_URL}/api/ats/generar-masivo?mesDesde=${mesDesde}&mesHasta=${mesHasta}&anio=${anioMasivo}`,
        { headers: headers(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ATS_${anioMasivo}_${mesDesde.padStart(2, '0')}-${mesHasta.padStart(2, '0')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMasivo('Error al descargar ZIP');
    }
    setLoadingMasivo(false);
  };

  const meses = [
    { v: '1', l: 'Enero' }, { v: '2', l: 'Febrero' }, { v: '3', l: 'Marzo' },
    { v: '4', l: 'Abril' }, { v: '5', l: 'Mayo' }, { v: '6', l: 'Junio' },
    { v: '7', l: 'Julio' }, { v: '8', l: 'Agosto' }, { v: '9', l: 'Septiembre' },
    { v: '10', l: 'Octubre' }, { v: '11', l: 'Noviembre' }, { v: '12', l: 'Diciembre' },
  ];

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>ATS - Anexo Transaccional Simplificado</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Genere el archivo XML del ATS para subir al portal del SRI. Incluye ventas, compras y retenciones del periodo seleccionado.
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Mensual (1 mes)" />
          <Tab label="Semestral (RIMPE)" />
          <Tab label="Masivo (ZIP)" />
        </Tabs>

        {/* TAB INDIVIDUAL */}
        {tab === 0 && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Mes</InputLabel>
                <Select value={mes} label="Mes" onChange={(e) => setMes(e.target.value)}>
                  {meses.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Anio</InputLabel>
                <Select value={anio} label="Anio" onChange={(e) => setAnio(e.target.value)}>
                  {[2024, 2025, 2026, 2027].map(a => <MenuItem key={a} value={String(a)}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                onClick={generar} disabled={loading} size="large">
                {loading ? 'Generando...' : 'Generar ATS'}
              </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {resultado && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Resumen ATS — {resultado.resumen?.periodo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {resultado.resumen?.empresa} | RUC: {resultado.resumen?.ruc}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ventas</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">{resultado.resumen?.ventas?.cantidad || 0}</Typography>
                    <Typography variant="body2">{fmt(resultado.resumen?.ventas?.total)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Compras</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">{resultado.resumen?.compras?.cantidad || 0}</Typography>
                    <Typography variant="body2">{fmt(resultado.resumen?.compras?.total)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ret. Recibidas</Typography>
                    <Typography variant="h5" fontWeight={700} color="warning.main">{resultado.resumen?.retenciones?.cantidad || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Ret. Emitidas: {resultado.resumen?.retenciones?.emitidas || 0}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Anulados</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">{resultado.resumen?.anulados?.cantidad || 0}</Typography>
                  </Paper>
                </Stack>

                <Button variant="contained" color="success" size="large" startIcon={<Download />}
                  onClick={descargarXML} sx={{ px: 4, py: 1.5 }}>
                  DESCARGAR XML (ATS_{anio}_{mes.padStart(2, '0')}.xml)
                </Button>

                <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Vista previa del XML:</Typography>
                  <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                    {resultado.xml?.substring(0, 3000)}{resultado.xml?.length > 3000 ? '\n...(truncado)' : ''}
                  </pre>
                </Paper>
              </>
            )}
          </>
        )}

        {/* TAB SEMESTRAL (RIMPE) */}
        {tab === 1 && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Genera un solo archivo XML con todas las transacciones de 6 meses (semestral).
              Solo para contribuyentes <strong>RIMPE</strong> (Régimen Simplificado para Emprendedores y Negocios Populares).
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Semestre</InputLabel>
                <Select value={semestre} label="Semestre" onChange={(e) => setSemestre(e.target.value)}>
                  <MenuItem value="06">1er Semestre (Enero - Junio)</MenuItem>
                  <MenuItem value="12">2do Semestre (Julio - Diciembre)</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Anio</InputLabel>
                <Select value={anioSemestral} label="Anio" onChange={(e) => setAnioSemestral(e.target.value)}>
                  {[2024, 2025, 2026, 2027].map(a => <MenuItem key={a} value={String(a)}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={loadingSemestral ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                onClick={generarSemestral} disabled={loadingSemestral} size="large">
                {loadingSemestral ? 'Generando...' : 'Generar ATS Semestral'}
              </Button>
            </Stack>

            {errorSemestral && <Alert severity="error" sx={{ mb: 2 }}>{errorSemestral}</Alert>}

            {resultadoSemestral && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Resumen ATS Semestral — {semestre === '06' ? 'Ene-Jun' : 'Jul-Dic'} {anioSemestral}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {resultadoSemestral.resumen?.empresa} | RUC: {resultadoSemestral.resumen?.ruc}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ventas</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">{resultadoSemestral.resumen?.ventas?.cantidad || 0}</Typography>
                    <Typography variant="body2">{fmt(resultadoSemestral.resumen?.ventas?.total)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Compras</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">{resultadoSemestral.resumen?.compras?.cantidad || 0}</Typography>
                    <Typography variant="body2">{fmt(resultadoSemestral.resumen?.compras?.total)}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ret. Recibidas</Typography>
                    <Typography variant="h5" fontWeight={700} color="warning.main">{resultadoSemestral.resumen?.retenciones?.cantidad || 0}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Anulados</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">{resultadoSemestral.resumen?.anulados?.cantidad || 0}</Typography>
                  </Paper>
                </Stack>

                <Button variant="contained" color="success" size="large" startIcon={<Download />}
                  onClick={descargarXMLSemestral} sx={{ px: 4, py: 1.5 }}>
                  DESCARGAR XML (AT{semestre}{anioSemestral}.xml)
                </Button>

                <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Vista previa del XML:</Typography>
                  <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                    {resultadoSemestral.xml?.substring(0, 3000)}{resultadoSemestral.xml?.length > 3000 ? '\n...(truncado)' : ''}
                  </pre>
                </Paper>
              </>
            )}
          </>
        )}

        {/* TAB MASIVO */}
        {tab === 2 && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Genera un archivo ZIP con un XML por cada mes del rango seleccionado. Cada XML es independiente y se puede subir por separado al SRI.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Mes desde</InputLabel>
                <Select value={mesDesde} label="Mes desde" onChange={(e) => setMesDesde(e.target.value)}>
                  {meses.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Mes hasta</InputLabel>
                <Select value={mesHasta} label="Mes hasta" onChange={(e) => setMesHasta(e.target.value)}>
                  {meses.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Anio</InputLabel>
                <Select value={anioMasivo} label="Anio" onChange={(e) => setAnioMasivo(e.target.value)}>
                  {[2024, 2025, 2026, 2027].map(a => <MenuItem key={a} value={String(a)}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={loadingMasivo ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                onClick={generarMasivoPreview} disabled={loadingMasivo || parseInt(mesDesde) > parseInt(mesHasta)} size="large">
                {loadingMasivo ? 'Generando...' : 'Vista Previa'}
              </Button>
            </Stack>

            {parseInt(mesDesde) > parseInt(mesHasta) && (
              <Alert severity="warning" sx={{ mb: 2 }}>El mes inicial debe ser menor o igual al mes final.</Alert>
            )}
            {errorMasivo && <Alert severity="error" sx={{ mb: 2 }}>{errorMasivo}</Alert>}

            {resultadoMasivo && (
              <>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Resumen masivo — {resultadoMasivo.rango}
                  </Typography>
                  <Chip label={`${resultadoMasivo.cantidadArchivos} archivos`} color="primary" size="small" />
                </Stack>

                <Table size="small" sx={{ mb: 3 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Mes</strong></TableCell>
                      <TableCell align="right"><strong>Ventas</strong></TableCell>
                      <TableCell align="right"><strong>Compras</strong></TableCell>
                      <TableCell align="right"><strong>Ret. Recibidas</strong></TableCell>
                      <TableCell align="right"><strong>Ret. Emitidas</strong></TableCell>
                      <TableCell align="right"><strong>Anulados</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultadoMasivo.resumen?.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{meses.find(m => m.v === String(parseInt(r.mes)))?.l || r.mes}</TableCell>
                        <TableCell align="right">{r.ventas?.cantidad || 0} ({fmt(r.ventas?.total)})</TableCell>
                        <TableCell align="right">{r.compras?.cantidad || 0} ({fmt(r.compras?.total)})</TableCell>
                        <TableCell align="right">{r.retenciones?.cantidad || 0}</TableCell>
                        <TableCell align="right">{r.retenciones?.emitidas || 0}</TableCell>
                        <TableCell align="right">{r.anulados?.cantidad || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Button variant="contained" color="success" size="large" startIcon={<FolderZip />}
                  onClick={descargarZIP} disabled={loadingMasivo} sx={{ px: 4, py: 1.5 }}>
                  {loadingMasivo ? 'Descargando...' : `DESCARGAR ZIP (${resultadoMasivo.cantidadArchivos} XMLs)`}
                </Button>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
